import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export interface CustomerBalance {
  customerId: string;
  totalInvoiced: number;
  totalPaid: number;
  totalOutstanding: number;
  totalCredits: number;
  overdueAmount: number;
  hasOverdue: boolean;
}

/**
 * Fetches balance information for a single customer
 */
export const useCustomerBalance = (customerId: string | undefined) => {
  return useQuery({
    queryKey: ['customerBalance', customerId],
    queryFn: async (): Promise<CustomerBalance | null> => {
      if (!customerId) return null;

      // Fetch all invoices for the customer
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select('id, total_amount, paid_amount, status, due_date')
        .eq('customer_id', customerId)
        .neq('status', 'cancelled');

      if (invoicesError) throw invoicesError;

      // Fetch customer credits
      const { data: credits, error: creditsError } = await supabase
        .from('customer_credits')
        .select('remaining_amount')
        .eq('customer_id', customerId)
        .eq('status', 'active');

      if (creditsError) throw creditsError;

      const now = new Date();
      
      const totalInvoiced = invoices?.reduce((sum, inv) => sum + (inv.total_amount || 0), 0) || 0;
      const totalPaid = invoices?.reduce((sum, inv) => sum + (inv.paid_amount || 0), 0) || 0;
      const totalOutstanding = totalInvoiced - totalPaid;
      const totalCredits = credits?.reduce((sum, c) => sum + (c.remaining_amount || 0), 0) || 0;
      
      // Calculate overdue amount
      const overdueAmount = invoices
        ?.filter(inv => {
          if (inv.status === 'paid' || inv.status === 'cancelled') return false;
          if (!inv.due_date) return false;
          return new Date(inv.due_date) < now;
        })
        .reduce((sum, inv) => sum + ((inv.total_amount || 0) - (inv.paid_amount || 0)), 0) || 0;

      return {
        customerId,
        totalInvoiced,
        totalPaid,
        totalOutstanding,
        totalCredits,
        overdueAmount,
        hasOverdue: overdueAmount > 0
      };
    },
    enabled: !!customerId,
    staleTime: 30000, // Cache for 30 seconds
  });
};

/**
 * Fetches balance information for multiple customers at once
 * More efficient than calling useCustomerBalance multiple times
 */
export const useCustomerBalances = (customerIds: string[]) => {
  return useQuery({
    queryKey: ['customerBalances', customerIds.sort().join(',')],
    queryFn: async (): Promise<Map<string, CustomerBalance>> => {
      if (customerIds.length === 0) return new Map();

      // Fetch all invoices for these customers
      const { data: invoices, error: invoicesError } = await supabase
        .from('invoices')
        .select('id, customer_id, total_amount, paid_amount, status, due_date')
        .in('customer_id', customerIds)
        .neq('status', 'cancelled');

      if (invoicesError) throw invoicesError;

      // Fetch customer credits
      const { data: credits, error: creditsError } = await supabase
        .from('customer_credits')
        .select('customer_id, remaining_amount')
        .in('customer_id', customerIds)
        .eq('status', 'active');

      if (creditsError) throw creditsError;

      const now = new Date();
      const balanceMap = new Map<string, CustomerBalance>();

      // Initialize all customers with zero balances
      customerIds.forEach(id => {
        balanceMap.set(id, {
          customerId: id,
          totalInvoiced: 0,
          totalPaid: 0,
          totalOutstanding: 0,
          totalCredits: 0,
          overdueAmount: 0,
          hasOverdue: false
        });
      });

      // Process invoices
      invoices?.forEach(inv => {
        const balance = balanceMap.get(inv.customer_id);
        if (balance) {
          balance.totalInvoiced += inv.total_amount || 0;
          balance.totalPaid += inv.paid_amount || 0;
          balance.totalOutstanding = balance.totalInvoiced - balance.totalPaid;
          
          // Check for overdue
          if (inv.status !== 'paid' && inv.due_date && new Date(inv.due_date) < now) {
            const outstanding = (inv.total_amount || 0) - (inv.paid_amount || 0);
            balance.overdueAmount += outstanding;
            balance.hasOverdue = balance.overdueAmount > 0;
          }
        }
      });

      // Process credits
      credits?.forEach(credit => {
        const balance = balanceMap.get(credit.customer_id);
        if (balance) {
          balance.totalCredits += credit.remaining_amount || 0;
        }
      });

      return balanceMap;
    },
    enabled: customerIds.length > 0,
    staleTime: 30000, // Cache for 30 seconds
  });
};
